import { create } from 'zustand';

import {
  API,
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import { queryClient } from '@/api/queryClient';
import type { CreateTaskPayload } from '@/components/CreateSheet/CreateEditTaskSheet';
import { useAuth } from '@/store/auth/auth.store';
import type {
  TaskDocument,
  TaskParticipant,
  TaskResponse,
  TasksFilter,
  TasksListResponse,
  TaskStatusUpdatePayload,
} from '@/store/tasks/tasks.dto.ts';
import { TaskParticipantType, TaskStatus } from '@/store/tasks/tasks.dto.ts';
import { taskKeys } from '@/store/tasks/tasks.queries';
import { hasAnyRole, UserRole } from '@/utils/roles';

import {
  addTaskParticipants,
  attachTaskDocuments,
  deleteTaskDocument,
  deleteTaskParticipants,
  fetchTaskById,
  fetchTasks,
  getTasksByEventId,
  getTasksByResponsible,
  shouldIncludeServiceTasks,
  shouldShowOnlyCurrentServiceTask,
} from './tasks.api';

/**
 * Anything that writes a task marks the cached task lists stale.
 *
 * The store keeps its own optimistic copy of `tasksList` for the surfaces that read it,
 * but the maintenance hub, the equipment badges and the tasks pages read react-query
 * instead — and a modal that changes a status has no idea which of the two is behind it.
 * Announcing it here rather than at each call site is what keeps a tick in the task modal
 * from leaving a stale count on the page underneath, whichever page that is.
 *
 * The client is the module singleton, so this works outside React too.
 */
const invalidateTaskQueries = () => {
  void queryClient.invalidateQueries({ queryKey: taskKeys.all });
};

/**
 * The task was created, but the people who were supposed to see it could not be
 * attached to it. Distinct from a creation failure: retrying the create would
 * leave a duplicate task behind.
 */
export class TaskParticipantsNotAttachedError extends Error {
  constructor(readonly cause: unknown) {
    super('Couldn’t add the crew. Please open the task.');
    this.name = 'TaskParticipantsNotAttachedError';
  }
}

/**
 * The task was created, but its files did not make it up. Same reason as above:
 * the task is there, so the fix is to attach them again, not to create it again.
 */
export class TaskDocumentsNotAttachedError extends Error {
  constructor(readonly cause: unknown) {
    super('Couldn’t attach the files. Please open the task.');
    this.name = 'TaskDocumentsNotAttachedError';
  }
}

/**
 * Activity echoes a blank `supervisor: {}` on tasks that have none (AI-generated
 * tasks stopped getting one), but its update validation treats these fields as
 * optional-yet-complete: absent or null passes, while an empty object fails on
 * the nested Name-required check. Blank participants must go out as null.
 */
const nullifyBlankParticipant = <T>(value: T): T | null => {
  if (!value || typeof value !== 'object') return value;
  const participant = value as Partial<TaskParticipant>;
  return participant.person_id || participant.name ? value : null;
};

const isServiceProviderUser = (): boolean => {
  const session = useAuth.getState().session;
  return hasAnyRole(session?.userRoles, [UserRole.SERVICE_PROVIDER]);
};

/**
 * A task's files live on the task, so both copies of it — the row in the list and
 * the open one — have to hear about an attach or a detach.
 */
const writeTaskDocuments =
  (id: string, next: (documents: TaskDocument[]) => TaskDocument[]) =>
  (state: TasksState): Partial<TasksState> => {
    const patch: Partial<TasksState> = {};

    if (state.tasksList) {
      patch.tasksList = {
        ...state.tasksList,
        items: state.tasksList.items.map((item) =>
          item.id === id ? { ...item, documents: next(item.documents ?? []) } : item,
        ),
      };
    }

    if (state.currentTask?.id === id) {
      patch.currentTask = {
        ...state.currentTask,
        documents: next(state.currentTask.documents ?? []),
      };
    }

    return patch;
  };

type TasksState = {
  tasksList: TasksListResponse | null;
  currentTask: TaskResponse | null;
  tasksCount: number;
  isLoading: boolean;
  getAllTasks: () => Promise<void>;
  getTasks: (filter: TasksFilter, append?: boolean) => Promise<TaskResponse[] | undefined>;
  getTasksByResponsible(responsibleId: string): Promise<TaskResponse[]>;
  getTasksByEventId(eventId: string): Promise<TaskResponse[]>;
  clearTasks: () => void;
  getTasksByFilter: (filter: TasksFilter) => Promise<TaskResponse[]>;
  getTaskById: (id: string) => Promise<TaskResponse>;
  createTask: (body: CreateTaskPayload) => Promise<TaskResponse>;
  updateTask: (body: TaskResponse) => Promise<void>;
  setTaskDone: (id: string) => Promise<void>;
  removeTask: (id: string) => Promise<void>;
  cancelTask: (id: string) => Promise<void>;
  setParticipant: (taskId: string, spId: string, name?: string) => Promise<unknown>;
  updateTaskStatus: (payload: TaskStatusUpdatePayload) => Promise<void>;
  addTaskParticipants: (id: string, participants: TaskParticipant[]) => Promise<void>;
  deleteTaskParticipants: (id: string, participantIds: string[]) => Promise<void>;
  attachTaskDocuments: (id: string, files: File[]) => Promise<TaskDocument[]>;
  deleteTaskDocument: (id: string, documentId: string) => Promise<void>;
};

export const useTasks = create<TasksState>((set, get) => ({
  tasksList: null,
  currentTask: null,
  tasksCount: 0,
  isLoading: false,
  getAllTasks: async () => {
    set({ isLoading: true });
    const includeServiceTasks = shouldIncludeServiceTasks();
    const includeOnlyCurrentServiceTask = shouldShowOnlyCurrentServiceTask();
    const isSP = isServiceProviderUser();
    const participantStatusQuery = isSP
      ? '&participant_status=unknown&participant_status=accepted&participant_status=maybe'
      : '';
    const query = `limit=1000&page=1${participantStatusQuery}${includeServiceTasks ? '&include_service_tasks=true' : ''}${includeOnlyCurrentServiceTask ? '&only_current_service_tasks=true' : ''}`;
    try {
      const response = await getRequest<TasksListResponse>(API.ACTIVITY, `/tasks?${query}`);
      set({
        tasksList: response.data,
        tasksCount: response.data.count,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  getTasks: async (filter: TasksFilter, append = false) => {
    const data = await fetchTasks(filter);
    const items = data.items;

    set((state) => {
      if (append && state.tasksList) {
        return {
          tasksList: {
            items: [...state.tasksList.items, ...items],
            count: data.count,
          },
          tasksCount: data.count,
        };
      }

      return {
        tasksList: data,
        tasksCount: data.count,
      };
    });

    return items;
  },

  clearTasks: () => {
    set({ tasksList: null, tasksCount: 0 });
  },

  getTasksByFilter: async (filter: TasksFilter) => {
    const data = await fetchTasks(filter);
    return data.items;
  },

  getTaskById: async (id: string) => {
    const task = await fetchTaskById(id);
    set({ currentTask: task });
    return task;
  },

  createTask: async (body: CreateTaskPayload) => {
    // Files can only be uploaded against a task that exists, so they travel with
    // the payload and are sent separately once the task has an id — they are no
    // part of the JSON body the create endpoint takes.
    const { documents, ...taskBody } = body;
    const response = await postRequest<TaskResponse, Omit<CreateTaskPayload, 'documents'>>(
      API.ACTIVITY,
      `/tasks`,
      taskBody,
    );

    set((state) => {
      const createdTask = response.data;

      if (!state.tasksList) {
        return {
          tasksList: {
            items: [createdTask],
            count: 1,
          },
          currentTask: state.currentTask,
          tasksCount: 1,
        };
      }

      const nextItems = [createdTask, ...state.tasksList.items];

      return {
        tasksList: {
          ...state.tasksList,
          items: nextItems,
          count: state.tasksList.count + 1,
        },
        currentTask: state.currentTask,
        tasksCount: state.tasksCount + 1,
      };
    });

    invalidateTaskQueries();

    // Both of these hang off a task that already exists, so a failure in either is
    // not a failed creation — retrying the create would leave a duplicate task
    // behind. Each is reported as its own error type so callers can say which half
    // didn't land instead of "failed to create task", and one failing doesn't keep
    // the other from being tried.
    let failure: Error | null = null;

    if (documents?.length) {
      try {
        await get().attachTaskDocuments(response.data.id, documents);
      } catch (err) {
        failure = new TaskDocumentsNotAttachedError(err);
      }
    }

    if (body.visibility !== 'private' && body.participants && body.participants.length > 0) {
      try {
        await addTaskParticipants(response.data.id, body.participants);
      } catch (err) {
        // Reported over a file failure: a task the crew cannot see is the worse
        // half to leave unsaid.
        failure = new TaskParticipantsNotAttachedError(err);
      }
    }

    if (failure) throw failure;

    return response.data;
  },

  updateTask: async (body: TaskResponse) => {
    const raw = body as TaskResponse & { supervisor?: TaskParticipant | null };
    const payload = {
      ...raw,
      supervisor: nullifyBlankParticipant(raw.supervisor),
      responsible: nullifyBlankParticipant(raw.responsible),
    };
    const response = await putRequest<TaskResponse, TaskResponse>(API.ACTIVITY, `/tasks`, payload);

    set((state) => {
      const updatedTask = response.data;

      let nextTasksList = state.tasksList;

      if (nextTasksList) {
        nextTasksList = {
          ...nextTasksList,
          items: nextTasksList.items.map((item) =>
            item.id === updatedTask.id ? updatedTask : item,
          ),
        };
      }

      const nextCurrentTask =
        state.currentTask && state.currentTask.id === updatedTask.id
          ? updatedTask
          : state.currentTask;

      return {
        tasksList: nextTasksList,
        currentTask: nextCurrentTask,
        tasksCount: state.tasksCount,
      };
    });

    invalidateTaskQueries();
  },

  setTaskDone: async (id: string) => {
    await patchRequest(API.ACTIVITY, `/tasks/${id}/done`, {});

    set((state) => {
      if (!state.tasksList) return {};

      const updatedItems = state.tasksList.items.map((item) =>
        item.id === id ? { ...item, status: TaskStatus.COMPLETED } : item,
      );

      return {
        tasksList: {
          ...state.tasksList,
          items: updatedItems,
        },
      };
    });

    invalidateTaskQueries();
  },

  removeTask: async (id: string) => {
    await deleteRequest<TaskResponse>(API.ACTIVITY, `/tasks/${id}`);

    set((state) => {
      if (!state.tasksList) {
        return {
          currentTask: state.currentTask && state.currentTask.id === id ? null : state.currentTask,
        };
      }

      const nextItems = state.tasksList.items.filter((item) => item.id !== id);
      const nextCount = Math.max(0, state.tasksList.count - 1);
      const nextTasksCount = Math.max(0, state.tasksCount - 1);

      return {
        tasksList: {
          ...state.tasksList,
          items: nextItems,
          count: nextCount,
        },
        currentTask: state.currentTask && state.currentTask.id === id ? null : state.currentTask,
        tasksCount: nextTasksCount,
      };
    });

    invalidateTaskQueries();
  },

  cancelTask: async (id: string) => {
    await patchRequest(API.ACTIVITY, `/tasks/${id}/participant/status`, {
      status: 'denied',
    });

    set((state) => {
      const nextState: Partial<TasksState> = {};

      if (state.tasksList) {
        const updatedItems = state.tasksList.items.map((item) =>
          item.id === id ? { ...item, status: TaskStatus.DENIED } : item,
        );
        nextState.tasksList = {
          ...state.tasksList,
          items: updatedItems,
        };
      }

      if (state.currentTask?.id === id) {
        nextState.currentTask = { ...state.currentTask, status: TaskStatus.DENIED };
      }

      return nextState;
    });

    invalidateTaskQueries();
  },

  setParticipant: async (taskId: string, spId: string, name?: string) => {
    const participant: TaskParticipant = {
      person_id: spId,
      type: TaskParticipantType.SERVICE_PROVIDER,
      name,
    };
    const mergeParticipants = (participants?: TaskParticipant[]): TaskParticipant[] => {
      const base = participants ?? [];
      const withoutDuplicates = base.filter(
        (item) => !(item.person_id === participant.person_id && item.type === participant.type),
      );
      return [...withoutDuplicates, participant];
    };

    const existingParticipants = (() => {
      const { currentTask, tasksList } = get();
      if (currentTask?.id === taskId) return currentTask.participants;
      return tasksList?.items.find((item) => item.id === taskId)?.participants;
    })();

    const participantsPayload = mergeParticipants(existingParticipants);

    const response = await patchRequest(API.ACTIVITY, `/tasks/${taskId}/participants`, {
      participants: participantsPayload,
    });

    set((state) => {
      const nextState: Partial<TasksState> = {};

      if (state.tasksList) {
        nextState.tasksList = {
          ...state.tasksList,
          items: state.tasksList.items.map((task) =>
            task.id === taskId
              ? { ...task, participants: mergeParticipants(task.participants) }
              : task,
          ),
        };
      }

      if (state.currentTask?.id === taskId) {
        nextState.currentTask = {
          ...state.currentTask,
          participants: mergeParticipants(state.currentTask.participants),
        };
      }

      return nextState;
    });

    invalidateTaskQueries();

    return response;
  },

  updateTaskStatus: async (payload: TaskStatusUpdatePayload) => {
    await patchRequest<void, TaskStatusUpdatePayload>(
      API.ACTIVITY,
      `/tasks/${payload.id}/status`,
      payload,
    );

    set((state) => {
      const nextState: Partial<TasksState> = {};

      if (state.tasksList) {
        nextState.tasksList = {
          ...state.tasksList,
          items: state.tasksList.items.map((item) =>
            item.id === payload.id ? { ...item, status: payload.status } : item,
          ),
        };
      }

      if (state.currentTask?.id === payload.id) {
        nextState.currentTask = { ...state.currentTask, status: payload.status };
      }

      return nextState;
    });

    invalidateTaskQueries();
  },
  getTasksByResponsible: async (responsibleId: string) => {
    return await getTasksByResponsible(responsibleId);
  },
  getTasksByEventId: async (eventId: string) => {
    return await getTasksByEventId(eventId);
  },
  addTaskParticipants: async (id: string, participants: TaskParticipant[]) => {
    await addTaskParticipants(id, participants);
  },
  deleteTaskParticipants: async (id: string, participantIds: string[]) => {
    await deleteTaskParticipants(id, participantIds);
  },

  attachTaskDocuments: async (id: string, files: File[]) => {
    await attachTaskDocuments(id, files);
    // The upload answers with the files it stored, which is not the same thing as
    // the task's list — activity appends. Reading the task back is what makes the
    // list here the whole list.
    const { data } = await getRequest<TaskResponse>(API.ACTIVITY, `/tasks/${id}`);
    const documents = data.documents ?? [];
    set(writeTaskDocuments(id, () => documents));
    invalidateTaskQueries();
    return documents;
  },

  deleteTaskDocument: async (id: string, documentId: string) => {
    await deleteTaskDocument(id, documentId);
    set(writeTaskDocuments(id, (docs) => docs.filter((doc) => doc.id !== documentId)));
    invalidateTaskQueries();
  },
}));
